import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

import { AuditComparison } from '@/components/audit/AuditComparison';
import { AuditNavigator } from '@/components/audit/AuditNavigator';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import type { ParkAndRideFacility } from '@/modules/api/backend';
import {
  backendApiParkAndRideFacilitiesFindAllAuditsOptions,
  backendApiParkAndRideFacilitiesFindOneAuditOptions,
} from '@/modules/api/backend/@tanstack/react-query.gen';

const i18n: Record<Exclude<keyof ParkAndRideFacility, '__caslSubjectType__'>, string> = {
  capacityClass: 'Kapazitätsklasse',
  capacityDate: 'Datum der Auslastung',
  capacityPercentage: 'Kapazität (%)',
  city: 'Ort',
  createdAt: 'Erstellt',
  dataSource: 'Datenquelle',
  deletedAt: 'Gelöscht',
  entranceHeight: 'Einfahrtshöhe',
  entranceHouseNumber: 'Hausnummer',
  entranceProhibited: 'Hausnummer 2',
  entranceSecondaryHouseNumber: '',
  entranceSecondaryStreet: 'Zufahrtsstraße 2',
  entranceStreet: 'Zufahrtsstraße 1',
  entranceWidth: 'Einfahrtsbreite',
  externalId: 'Externe ID',
  facilityType: 'Art der Parkgelegenheit',
  freeToUse: 'Kostenfrei nutzbar',
  geometry: 'Standort',
  id: 'ID',
  isAlwaysOpen: '24 h geöffnet',
  isParkingUnlimited: 'Parkdauer unbegrenzt',
  legacyId: 'Bestandssystem ID',
  lighting: 'Beleuchtung',
  managementUrl: 'URL zur Bewirtschaftung',
  name: 'Name',
  numParkingSpaceDisabled: 'Behindertenstellplätze',
  numParkingSpaceMotorcycle: 'Motorradstellplätze',
  numParkingSpaceTotal: 'Gesamtstellplätze',
  numParkingSpaceWomen: 'Frauenstellplätze',
  openingHours: 'Öffnungszeiten',
  organization: 'Organisation',
  organizationId: 'Organisations ID',
  parkingDuration: 'Parkdauer',
  parkingSpaceLastUpdate: 'Letzte Aktualisierung',
  parkingSpaceMarking: 'Markierung der Stellplätze',
  parkingSurface: 'Oberfläche',
  postalCode: 'Postleitzahl',
  prOfficial: 'Offizielle P+R-Anlage',
  prStatusNote: 'Hinweis',
  prUsageComment: 'Kommentar',
  publication: 'Parkraumangebot öffentlich sichtbar',
  rmvId: 'RMV-Nummer',
  station: 'Station',
  stationId: 'Stations ID',
  toStationAccessibility: 'Barrierefreiheit zur Station',
  tracingCapacity: 'Ermittlung Kapazität',
  tracingParkingSpace: 'Ermittlung Stellplätze',
  trafficSign: 'Beschilderung der Anlage',
  trafficSignAdditional: 'Beschilderung Zusatztafel',
  typeOfManagement: 'Art der Bewirtschaftung',
  updatedAt: 'Geändert',
  usable: 'Anlage nutzbar',
  usageExtent: 'Ausmaß der Nicht-P+R-Nutzung',
};

export const Route = createFileRoute('/_authenticated/management/stationless/$parkAndRideFacilityId/audits')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: ParkingFacilityAuditsRoute,
  beforeLoad: () => ({ breadcrumb: 'Bearbeitungsverlauf' }),
});

function ParkingFacilityAuditsRoute() {
  const { parkAndRideFacilityId } = Route.useParams();

  const { data: audits } = useSuspenseQuery(
    backendApiParkAndRideFacilitiesFindAllAuditsOptions({
      path: {
        parkAndRideFacilityId,
      },
    }),
  );

  const [index, setIndex] = useState(audits.length - 1);

  const { data: oldAudit, isLoading: isLoadingOld } = useQuery({
    ...backendApiParkAndRideFacilitiesFindOneAuditOptions({
      path: {
        parkAndRideFacilityId,
        auditCreatedAt: audits[index - 1]?.createdAt as string,
      },
    }),
    enabled: !!audits[index - 1],
  });
  const { data: newAudit, isLoading: isLoadingNew } = useQuery({
    ...backendApiParkAndRideFacilitiesFindOneAuditOptions({
      path: {
        parkAndRideFacilityId,
        auditCreatedAt: audits[index]?.createdAt as string,
      },
    }),
    enabled: !!audits[index],
  });

  return (
    <Fragment>
      <AdministrationHeader
        label="Parkraumangebot Bearbeitungsverlauf"
        links={[
          { name: 'Bearbeiten', options: { to: '/management/stationless/$parkAndRideFacilityId' }, icon: 'PENCIL' },
        ]}
      />
      <AdministrationBody>
        {!isLoadingOld && !isLoadingNew && (
          <Fragment>
            <AuditNavigator entries={audits} index={index} onChange={setIndex} />
            <AuditComparison i18n={i18n} newAudit={newAudit} oldAudit={oldAudit} />
          </Fragment>
        )}
      </AdministrationBody>
    </Fragment>
  );
}
